import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import FileInput from "../../components/shared/FileInput";
import { Input, Label } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { getAllGrantRelatedDocs } from "../../api/grantLetter";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { NewCollapsibleComponent as CollapsibleComponent } from "../../components/shared/NewCollapsibleComponent";
import { formatDisplayDate } from "../../utils/date";
import { Grant } from "../../types/Grant";
import { downloadFileFromURL, downloadS3File } from "../../utils/DownloadFile";
import StatusLabel from "./StatusLabel";
import Tooltip from "../../components/shared/Tooltip";
import NewTooltip from "../../components/shared/NewToolTip";

export interface UploadedFile {
  name: string;
  type: string;
  size: number;
  base64: string;
  canEmployeeView?: boolean;
  id?: string;
  s3Url?: string;
  file?: Buffer;
  isUserUploaded?: boolean;
  tags?: string[];
  entityType?: string;
  documentType?: string;
  generatedOn?: Date;
}

export default function GrantDocumentDialog({
  grant,
  onClose,
}: {
  grant: Grant;
  onClose: () => void;
}) {
  // Create a ref for the preview section
  const previewRef = React.useRef<HTMLDivElement>(null);
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<UploadedFile | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Accordion state
  const [accordionOpened, setAccordionOpened] = useState<{
    [key: string]: boolean;
  }>({
    grantLevel: false,
    uploaded: false,
    amendments: true,
    employeeLevel: true,
  });

  // Fetch documents
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    getAllGrantRelatedDocs(`${grant.optionHolderId},${grant.employeeId}`)
      .then((docs) => {
        if (isMounted) setDocuments(docs);
      })
      .catch(() => {
        toast("Failed to fetch documents", { type: "error" });
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [grant]);

  const splitText =
    "Document issued when a stock split occurs, detailing the adjustment to the number of options and exercise price to reflect the split ratio.";
  const grantText =
    "Official document confirming the equity grant details including the number of options granted, vesting schedule, and exercise price.";

  const handleAccordionClick = (section: string) => {
    setAccordionOpened((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const grantDocs = documents.filter(
    (d) => d.entityType?.toLowerCase()?.toLowerCase()?.toLowerCase() === "grant"
  );
  const employeeDocs = documents.filter(
    (d) =>
      d.entityType?.toLowerCase()?.toLowerCase()?.toLowerCase() === "employee"
  );
  const uploadedDocs = documents.filter((d) => d.isUserUploaded);

  const validationSchema = Yup.object({
    name: Yup.string().required("Document Name is required"),
    entityType: Yup.string().required("Document Type is required"),
    file: Yup.mixed().required("File is required"),
  });

  const downloadFile = (doc: UploadedFile) => {
    if (doc.s3Url) {
      downloadFileFromURL(doc.s3Url, doc.name);
    } else {
      toast("No URL available for download", { type: "error" });
    }
  };

  const renderPreview = () => {
    if (!selectedDoc) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          Select a document to preview
        </div>
      );
    }

    if (selectedDoc.type === "application/pdf") {
      return <LazyPdfDocument url={selectedDoc.s3Url || ""} />;
    }

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 overflow-hidden">
        <Icon icon="mdi:file-download" width={64} className="text-gray-400" />
        <p className="text-gray-600">
          Preview not available for this file type
        </p>
        <ButtonPrimary
          onClick={() =>
            selectedDoc.s3Url && window.open(selectedDoc.s3Url, "_blank")
          }
        >
          Download Document
        </ButtonPrimary>
      </div>
    );
  };

  // Rest of your component remains the same...
  return (
    <VStack className="flex w-full h-[700px] bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <VStack className="flex flex-col gap-2 w-full">
        <VStack className="p-8 text-lg font-medium border-b gap-1">
          <h6 className="flex justify-between">
            Grant Documents — {grant.employeeName} · Grant ID:{" "}
            {grant.grantIdentifier}
            <span className="cursor-pointer" onClick={() => onClose()}>
              X
            </span>
          </h6>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>Grant Date: {formatDisplayDate(grant.grantDate)}</span>
            <span>·</span>
            <span className="px-2 py-1 rounded text-xs font-medium">
              <StatusLabel
                state={grant.optionHolderState}
                _className="text-[12px] p-1"
              />
            </span>
            {grant.grantLetterTemplateName && (
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                Template: {grant.grantLetterTemplateName}
              </span>
            )}
            {documents.length > 0 &&
              documents.some((doc) => doc.documentType === "SPLIT_LETTER") && (
                <span className="px-2 py-1 bg-[#003366] text-white rounded text-xs font-medium">
                  Split applied
                </span>
              )}
          </div>
        </VStack>
      </VStack>

      <HStack className="flex-1 min-h-0">
        <div className="w-[40%] border-r border-gray-200 flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <VStack className="gap-y-4">
              <CollapsibleComponent
                label="Grant-Level Documents"
                labelDetail=""
                isOpen={accordionOpened.grantLevel}
                onClick={() => handleAccordionClick("grantLevel")}
                numberOfItems={grantDocs.length}
              >
                <VStack className="gap-2">
                  {grantDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`p-4 cursor-pointer transition-all duration-200 rounded-lg border ${
                        selectedDoc?.id === doc.id
                          ? "bg-orange-50 border-orange-200"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedDoc(doc)}
                    >
                      <div className="flex items-start gap-3">
                        <VStack className="flex-1 min-w-0">
                          <HStack className="justify-between items-center align-middle">
                            <HStack className="align-middle text-center items-center gap-1">
                              <HStack className="font-medium text-gray-900 text-sm">
                                {doc.name && doc.name.includes("Split")
                                  ? "Split Letter"
                                  : doc.name}
                              </HStack>
                              <HStack>
                                <NewTooltip
                                  text={
                                    doc.name && doc.name.includes("Split")
                                      ? splitText
                                      : grantText
                                  }
                                  position="right"
                                  maxWidth="300px"
                                >
                                  <Icon
                                    icon="mdi:information-outline"
                                    width={14}
                                  />
                                </NewTooltip>
                              </HStack>
                            </HStack>
                            <HStack className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                {doc.generatedOn
                                  ? formatDisplayDate(doc.generatedOn)
                                  : ""}
                              </span>
                            </HStack>
                          </HStack>
                          <HStack className="flex items-center gap-2 text-center">
                            <Icon
                              icon="vscode-icons:file-type-pdf2"
                              width={20}
                              className="text-gray-500 mt-1"
                            />
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                              Grant-Specific
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                              {doc.isUserUploaded
                                ? "User-Uploaded"
                                : "System-Generated"}
                            </span>
                          </HStack>
                        </VStack>
                      </div>
                    </div>
                  ))}
                </VStack>
              </CollapsibleComponent>

              <CollapsibleComponent
                label="Employee-Level Documents"
                labelDetail=""
                isOpen={accordionOpened.employeeLevel}
                onClick={() => handleAccordionClick("employeeLevel")}
                numberOfItems={employeeDocs.length}
              >
                <div className="space-y-2 pt-2">
                  {employeeDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`p-4 cursor-pointer transition-all duration-200 rounded-lg border ${
                        selectedDoc?.id === doc.id
                          ? "bg-orange-50 border-orange-200"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedDoc(doc)}
                    >
                      <div className="flex items-start gap-3">
                        <VStack className="flex-1 min-w-0">
                          <HStack className="justify-between">
                            <HStack className="font-medium text-gray-900 text-sm truncate">
                              {doc.name && doc.name.includes("Split")
                                ? "Split Letter"
                                : doc.name}
                            </HStack>
                            <HStack className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Icon
                                  icon="mdi:information-outline"
                                  width={14}
                                />
                                {doc.generatedOn
                                  ? formatDisplayDate(doc.generatedOn)
                                  : ""}
                              </span>
                            </HStack>
                          </HStack>
                          <HStack className="flex items-center gap-2 text-center">
                            <Icon
                              icon="vscode-icons:file-type-pdf2"
                              width={20}
                              className="text-gray-500 mt-1"
                            />
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                              Employee-Specific
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                              {doc.isUserUploaded
                                ? "User-Uploaded"
                                : "System-Generated"}
                            </span>
                          </HStack>
                        </VStack>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleComponent>
            </VStack>
          </div>
        </div>

        <div className="w-[60%] flex flex-col h-full">
          <div className="px-6 py-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedDoc ? selectedDoc.name : "Preview of Letter"}
              </h2>
              <div className="flex items-center gap-3">
                {selectedDoc && (
                  <button
                    onClick={() => downloadFile(selectedDoc)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                  >
                    <Icon icon="mdi:download" width={18} />
                    Download
                  </button>
                )}
              </div>
            </div>
            {selectedDoc && (
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                <span>
                  Generated On:{" "}
                  {selectedDoc.generatedOn
                    ? formatDisplayDate(selectedDoc.generatedOn)
                    : ""}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                  {selectedDoc.isUserUploaded
                    ? "User-Uploaded"
                    : "System-Generated"}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
            {renderPreview()}
          </div>
        </div>
      </HStack>
    </VStack>
  );
}
